import {SpacesSelect} from '@github-ui/copilot-spaces/components/SpacesSelect'
import {useSpaceSelection} from '@github-ui/copilot-spaces/hooks/use-space-selection'
import {sendEvent} from '@github-ui/hydro-analytics'
import {testIdProps} from '@github-ui/test-id-props'
import type {CommandId} from '@github-ui/ui-commands'
import {CommandIconButton, ScopedCommands, START_PULL_REQUEST_BUTTON_TEXT} from '@github-ui/ui-commands'
import {useNavigate} from '@github-ui/use-navigate'
import {useSyntheticChange} from '@github-ui/use-synthetic-change'
import type {AnchorSide} from '@primer/behaviors'
import {PaperAirplaneIcon, PlusIcon, RocketIcon, SquareFillIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Spinner, useSafeTimeout} from '@primer/react'
import {clsx} from 'clsx'
import type {RefObject} from 'react'
import {memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {useAddMentionReferences} from '../hooks/use-add-mention-references'
import {useAddUrlReferences} from '../hooks/use-add-url-references'
import {useAvailableCommands} from '../hooks/use-available-commands'
import {isTaskCommand, useChatInput} from '../hooks/use-chat-input'
import {isIncompleteSlashCommand} from '../utils/chat-commands-helpers'
import {copilotChatTextAreaId} from '../utils/constants'
import type {
  CopilotChatCommandType,
  CopilotChatReference,
  CopilotChatRepo,
  ImageReference,
  ModeSupport,
  ThreadScopedFileReference,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {CopilotImageAttacher} from '../utils/copilot-image-attacher'
import {sendVisionErrorEvent} from '../utils/copilot-image-helpers'
import {copilotLocalStorage} from '../utils/copilot-local-storage'
import {CLIPBOARD_MIME_TYPE, CopilotTextAttacher} from '../utils/copilot-text-attacher'
import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {useIsCopilotSpacePath} from '../utils/custom-copilots-helpers'
import {useGetChatState} from '../utils/ObservableChatContext'
import {AttachmentMenu} from './AttachmentMenu'
import type {AttachmentMenuPanelType} from './AttachmentMenuPanelType'
import {ChatInputAutocomplete} from './autocomplete/ChatInputAutocomplete'
import styles from './ChatInput.module.css'
import {ChatInputPreview} from './ChatInputPreview'
import {ChatInputReferences} from './ChatInputReferences'
import {FullScreenDragOverlay} from './FullScreenDragOverlay'
import {McpToolMenu} from './McpToolMenu'
import {ModelPicker} from './ModelPicker'
import {PlanningIntentToggle} from './PlanningIntentToggle'
import {RepoBranchAgentPicker} from './repo-branch-agent-selector/RepoBranchAgentSelector'

/** When the user pastes text that is longer than this many characters, we will automatically convert it to a file. */
const PASTE_TEXT_AS_FILE_THRESHOLD = 1000

const LIMITED_KEYBINDING_COMMAND_IDS: CommandId[] = [
  'github:cancel',
  'copilot-chat:send-message',
  'copilot-chat:edit-last-message',
]

export type ChatInputRef = {
  setText: React.Dispatch<React.SetStateAction<string>>
}

export interface ChatInputProps {
  className?: string
  disableAutoFocus?: boolean
  disableSavedUserMessage?: boolean
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  chatInputRef?: React.RefObject<ChatInputRef | null>
  containerRef?: React.RefObject<HTMLFormElement | null>
  onSubmit?: (text: string, additionalReferences?: CopilotChatReference[]) => Promise<void>
  isStreaming?: boolean
  activeModeName?: string
  modes: [ModeSupport, ...ModeSupport[]]
  onClickMode?: (modeName: string) => void
  size?: 'small' | 'large'
  onSelectReference?: (reference: CopilotChatReference, event?: React.MouseEvent<HTMLAnchorElement>) => void
  onNewThreadSelected?: () => Promise<void>
  getReferenceVersion?: (reference: CopilotChatReference) => number | undefined
  /** Set of previously generated/referenced file names in this thread, to use as a reference for generating new file names. */
  existingFileNames?: Set<string>
  disabled?: boolean
  submitDisabled?: boolean
  figmaAuthUrl?: string
  extraToolbar?: React.ReactNode
  showPlanningToggle?: boolean
  initialReference?: CopilotChatReference
  onRemoveReference?: (reference: CopilotChatReference) => void
  isNewConversation?: boolean
  onChatCommandChange?: (command: CopilotChatCommandType | null) => void
  /** Set to true when rendered in a Portal to handle height issues */
  isInPortal?: boolean
  shouldClearTextOnCommandSubmit?: boolean
  shouldClearTextOnSubmit?: boolean
  slashCommandsEnabled?: boolean
  attachmentMenuSide?: AnchorSide
  storageKeySuffix?: string
  showStartPullRequestButton?: boolean
  customModelPicker?: React.ReactNode
}

export const ChatInput = (props: ChatInputProps) => {
  const state = useChatStateValues(
    'defaultRecipient',
    'isWaitingOnAttachment',
    'attachmentProcessingType',
    'isWaitingOnCopilot',
    'messages',
    'mode',
    'model',
    'selectedThreadID',
    'skillOptions',
    'slashCommandLoading',
    'thirdPartyMcpAllowed',
  )
  const {model} = state
  const getChatState = useGetChatState()
  const manager = useChatManager()
  const maxMessagesReached = manager.maxMessagesReached()
  const [pastedFile, setPastedFile] = useState<File | null>(null)
  const slashCommandLoading = state.slashCommandLoading.state === 'loading'
  const {safeSetTimeout} = useSafeTimeout()
  const {
    onSelectReference,
    containerRef,
    shouldClearTextOnCommandSubmit = true,
    shouldClearTextOnSubmit = true,
    slashCommandsEnabled = false,
  } = props
  const pastedFileNamePromise = useRef<Promise<string> | null>(null)
  const pastedFileName = useRef<string>('')
  const convertToFileShownLogged = useRef(false)
  const navigate = useNavigate()
  const {availableCommands, availableCommandKeys} = useAvailableCommands({enabled: slashCommandsEnabled})
  const commandModes = availableCommands.map(cmd => cmd.mode)
  const chatModes = copilotFeatureFlags.askModeDropdown && commandModes.length > 0 ? commandModes : props.modes
  const [activeMode, setActiveMode] = useState<ModeSupport>(() => {
    const savedModeName = copilotLocalStorage.getActiveMode()
    if (savedModeName) {
      const savedMode = chatModes.find(m => m.name === savedModeName)
      if (savedMode) return savedMode
    }
    return chatModes[0]!
  })
  const onClickModeCallback = props.onClickMode
  // Use custom hook to manage space selection logic
  const {activeCopilotSpaceId} = useSpaceSelection()

  const currentMode = copilotFeatureFlags.askModeDropdown
    ? activeMode
    : chatModes.find(m => m.name === props.activeModeName) || chatModes[0]!

  const onClickMode = useCallback(
    (modeName: string) => {
      onClickModeCallback?.(modeName)
      if (copilotFeatureFlags.askModeDropdown) {
        const mode = chatModes.find(m => m.name === modeName)
        if (mode) {
          setActiveMode(mode)
          copilotLocalStorage.setActiveMode(mode.name)
          sendEvent('dotcom_chat.activate', {target: 'MODE_SWITCH', mode: mode.name})
        }
      }
    },
    [chatModes, onClickModeCallback],
  )

  // Add initial reference to manager
  useEffect(() => {
    if (props.initialReference) {
      manager.addReference(props.initialReference, 'page-file-reference')
    }
  }, [props.initialReference, manager])

  const clearPastedFile = useCallback(
    (logDismiss = false) => {
      pastedFileNamePromise.current = null
      pastedFileName.current = ''
      convertToFileShownLogged.current = false
      if (pastedFile) {
        setPastedFile(null)
        if (logDismiss) {
          sendEvent('dotcom_chat.activate', {target: 'CONVERT_TO_FILE_DISMISS', mode: state.mode})
        }
      }
    },
    [pastedFile, state.mode],
  )

  const {
    typingHasStarted,
    textAreaRef,
    textareaPreviewRef,
    textAreaPreviewContainerRef,
    text,
    setText,
    isAgentTaskSubmitInProgress,
    handleSubmit,
    handleSubmitToNewThread,
    handleScroll,
    handleChange,
    handleStop,
    handleFocus,
    command,
    setLastUsedRepo,
    // agent tasks
    selectedRepo,
    setSelectedRepo,
    selectedBranch,
    setSelectedBranch,
    selectedAgent,
    setSelectedAgent,
    initialRepoNWO,
  } = useChatInput({
    isLoading: state.isWaitingOnCopilot || state.isWaitingOnAttachment || slashCommandLoading,
    onSubmit: props.onSubmit,
    textAreaRef: props.textAreaRef,
    clearPastedFile,
    onChatCommandChange: props.onChatCommandChange,
    disableSavedUserMessage: props.disableSavedUserMessage,
    isInPortal: props.isInPortal,
    shouldClearTextOnCommandSubmit,
    shouldClearTextOnSubmit,
    availableCommands,
    activeModeName: currentMode?.name,
    storageKeySuffix: props.storageKeySuffix,
  })

  // Get custom model picker from the active slash command if available, or use the prop
  const activeCommand = useMemo(() => availableCommands.find(cmd => cmd.key === command), [availableCommands, command])
  const customModelPicker = activeCommand?.customModelPicker ?? props.customModelPicker

  const disabled = props.disabled || isAgentTaskSubmitInProgress || maxMessagesReached

  useImperativeHandle(props.chatInputRef, () => ({setText}) satisfies ChatInputRef, [setText])

  const emitChange = useSyntheticChange({inputRef: textAreaRef, fallbackEventHandler: handleChange})
  const {replaceUrlWithReferenceMention} = useAddUrlReferences({
    figmaAuthUrl: props.figmaAuthUrl,
    textAreaRef,
    emitChange,
  })
  const {
    isLoading: mentionReferencesPending,
    addReferenceForMention,
    addReferencesForMentions,
  } = useAddMentionReferences()
  const isCopilotSpacePath = useIsCopilotSpacePath()
  const isCopilotSpace = activeCopilotSpaceId && isCopilotSpacePath

  // Check if the current text is an incomplete slash command (memoized to avoid duplicate computation)
  const isIncompleteCommand = useMemo(() => isIncompleteSlashCommand(text, command), [text, command])
  const isTask = isTaskCommand(activeMode?.name, command)

  const submitDisabled =
    slashCommandLoading ||
    mentionReferencesPending ||
    state.isWaitingOnAttachment ||
    !!props.submitDisabled ||
    (isTask && !selectedRepo && !selectedBranch) ||
    isAgentTaskSubmitInProgress ||
    isIncompleteCommand

  const textAreaEmpty = text.length === 0

  const stopResponse = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'CHAT_MESSAGE_STOP', mode: state.mode})
    void handleStop()
  }, [handleStop, state.mode])

  const sendMessage = useCallback(() => {
    if (submitDisabled) return
    const spaceIdString = activeCopilotSpaceId ? `${activeCopilotSpaceId.owner}/${activeCopilotSpaceId.id}` : ''
    sendEvent('dotcom_chat.activate', {
      target: 'CHAT_MESSAGE_SUBMIT',
      mode: state.mode,
      selectedThreadId: state.selectedThreadID,
      copilotSpace: spaceIdString,
      fromSpaceUI: isCopilotSpacePath,
    })

    if (pastedFile) {
      clearPastedFile(false)
    }
    void handleSubmit()
  }, [
    submitDisabled,
    activeCopilotSpaceId,
    state.mode,
    state.selectedThreadID,
    isCopilotSpacePath,
    pastedFile,
    handleSubmit,
    clearPastedFile,
  ])

  const sendMessageToNewThread = useCallback(() => {
    if (submitDisabled) return

    void handleSubmitToNewThread()
  }, [handleSubmitToNewThread, submitDisabled])

  const editLastMessage = useCallback(() => {
    const id = state.messages.findLast(m => m.role === 'user')?.id
    if (id) manager.startEditingMessage(id)
  }, [manager, state.messages])

  const placeholderText = (() => {
    if (state.isWaitingOnCopilot) return 'Copilot is responding…'
    if (state.defaultRecipient) return undefined
    if (maxMessagesReached) return 'Message limit reached. To continue chatting with Copilot, start a new conversation.'
    return currentMode.placeholder ?? 'Ask anything'
  })()

  if (state.defaultRecipient && text === '' && !typingHasStarted) {
    setText(`@${state.defaultRecipient} `)
  }

  const attachmentButtonRef = useRef<HTMLButtonElement>(null)

  const [attachmentMenuPanel, setAttachmentMenuPanel] = useState<AttachmentMenuPanelType | null>(null)
  const [lastAttachedRepo, setLastAttachedRepo] = useState<CopilotChatRepo | null>(null)
  const addAttachment = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU', mode: state.mode})
    setAttachmentMenuPanel(panel => (panel === 'attachment-types' ? null : 'attachment-types'))
  }, [state.mode, setAttachmentMenuPanel])

  const setDeepCodeSearch = useCallback(() => {
    sendEvent('dotcom_chat.activate', {target: 'DEEP_CODESEARCH', mode: state.mode})
    manager.dispatch({type: 'SET_DEEP_CODESEARCH', deepCodeSearch: !state.skillOptions?.deepCodeSearch})
  }, [manager, state.mode, state.skillOptions?.deepCodeSearch])

  // For now, we are focused on building out image support in immersive mode only, and only for supported models.
  const shouldShowImages = (state.mode === 'immersive' && !!model.capabilities?.supports?.vision) || false

  const handleFileAttachment = useCallback(
    async (files: Iterable<File>, uploadType: 'drag' | 'paste', namePromise?: Promise<string>, name?: string) => {
      const imageAttacher = new CopilotImageAttacher(getChatState(), manager)
      const textAttacher = new CopilotTextAttacher(manager, props.existingFileNames)
      const attachedFiles: Array<ThreadScopedFileReference | ImageReference> = []

      const hasImageFiles = [...files].some(file => file.type.startsWith('image/'))
      if (hasImageFiles && !model.capabilities?.supports?.vision) {
        manager.addAmbientError(
          `The ${model.displayName} model doesn't support answering questions about images. Select a different model and try again.`,
        )
        sendVisionErrorEvent('vision_not_supported_for_model', {
          modelId: model.id,
          uploadType,
        })
      }

      // Split the image files out of the other files so we can handle them separately.
      const [imageFiles, otherFiles] = CopilotImageAttacher.getAllowedFiles([...files], model)

      if (shouldShowImages) {
        const imageAttachments = await imageAttacher.addImageAttachments(imageFiles, uploadType)
        if (imageAttachments) attachedFiles.push(...imageAttachments)
      }

      const disallowedFileTypes = []
      for (const file of otherFiles) {
        if (await CopilotTextAttacher.isAttachable(file)) {
          const attachedFile = await textAttacher.addAttachment(file, namePromise, name)
          if (attachedFile) attachedFiles.push(attachedFile)
        } else disallowedFileTypes.push(file.type)
      }

      if (shouldShowImages && disallowedFileTypes.length > 0) {
        sendVisionErrorEvent('included_unsupported_file', {
          fileTypes: disallowedFileTypes.join(','),
          uploadType,
        })
      }
      return attachedFiles
    },
    [shouldShowImages, model, getChatState, manager, props.existingFileNames],
  )

  const handleConvertToFile = useCallback(async () => {
    if (!pastedFile) return
    const fileArray = [pastedFile]
    const attachments = await handleFileAttachment(
      fileArray,
      'paste',
      pastedFileNamePromise.current || undefined,
      pastedFileName.current,
    )
    if (attachments.length > 0 && attachments[0]?.type === 'thread-scoped-file') {
      const pastedText = await pastedFile.text()
      const newText = text.replaceAll(pastedText, `@${attachments[0].name}`)
      setText(newText)
      clearPastedFile(false)
      sendEvent('dotcom_chat.activate', {target: 'CONVERT_TO_FILE_CONVERT', mode: state.mode})
    }
  }, [pastedFile, handleFileAttachment, state.mode, text, setText, clearPastedFile])

  const handleConvertToFileDismiss = useCallback(() => {
    clearPastedFile(true)
  }, [clearPastedFile])

  // Log when the convert attachment is shown
  useEffect(() => {
    if (pastedFile && !state.isWaitingOnAttachment && !convertToFileShownLogged.current) {
      convertToFileShownLogged.current = true
      sendEvent('dotcom_chat.activate', {target: 'CONVERT_TO_FILE_TRIGGER', mode: state.mode})
    }
  }, [pastedFile, state.isWaitingOnAttachment, state.mode])

  const supportsReferenceAttachments = (currentMode.supportsReferenceAttachments ?? true) && command !== 'task'

  const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = event => {
    const clipboardData = event.clipboardData
    if (clipboardData?.items) {
      const fileArray: File[] = []

      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i]
        if (!item) continue

        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file) fileArray.push(file)
        } else if (
          item.kind === 'string' &&
          ['text', 'text/plain'].includes(item.type) // "text" for tests, "text/plain" for real clipboard
        ) {
          const pastedText = clipboardData.getData(item.type)

          if (
            pastedText.length > PASTE_TEXT_AS_FILE_THRESHOLD &&
            pastedText.length <= CopilotTextAttacher.getAttachmentSizeLimit()
          ) {
            // Run after the input value updates.
            safeSetTimeout(() => {
              convertToFileShownLogged.current = false

              const file = new File([pastedText], '', {type: CLIPBOARD_MIME_TYPE})
              const textAttacher = new CopilotTextAttacher(manager, props.existingFileNames)
              setPastedFile(file)
              const namePromise = textAttacher
                .generateFileName(pastedText, file.type)
                // eslint-disable-next-line github/no-then
                .then(name => (pastedFileName.current = name))
              pastedFileNamePromise.current = namePromise
            }, 1)
          } else if (supportsReferenceAttachments) {
            addReferencesForMentions(pastedText)

            const insertionIndex = textAreaRef.current?.selectionStart ?? 0
            // After the paste completes, process the pasted text for URLs. Doing it this way (as opposed to preventing
            // the paste and setting the value synchronously) preserves the undo stack, allowing users to undo the
            // URL replacing if they want
            setTimeout(() => replaceUrlWithReferenceMention([insertionIndex, insertionIndex + pastedText.length]))
          }
          return
        }
      }

      if (fileArray.length > 0) {
        event.preventDefault()
        void handleFileAttachment(fileArray, 'paste')
      }
    }
  }

  const handleFileDrop = useCallback(
    (files: FileList) => {
      void handleFileAttachment(files, 'drag')
    },
    [handleFileAttachment],
  )

  const onRepoAttach = useCallback(
    (repo: CopilotChatRepo | null) => {
      if (repo) {
        setLastUsedRepo(repo)
      }
      // Set last attached repo for multifile attach dialog, or clear out if null.
      setLastAttachedRepo(repo)
    },
    [setLastUsedRepo, setLastAttachedRepo],
  )

  const onCommandAutoComplete = useCallback(
    (commandKey: CopilotChatCommandType) => {
      const selectedCommand = availableCommands.find(cmd => cmd.key === commandKey)
      if (selectedCommand?.enabled?.() === false && selectedCommand.disabledAction) {
        selectedCommand.disabledAction(navigate)
        return
      }
      if (copilotFeatureFlags.askModeDropdown && selectedCommand) {
        setActiveMode(selectedCommand.mode)
        copilotLocalStorage.setActiveMode(selectedCommand.mode.name)
        sendEvent('dotcom_chat.activate', {target: 'MODE_SWITCH', mode: selectedCommand.mode.name, autoComplete: true})
      }
    },
    [availableCommands, navigate],
  )

  const stoppable = props.isStreaming || state.isWaitingOnCopilot
  const supportsAttachments = Boolean(currentMode.supportsAttachments) && command !== 'task'

  return (
    <div className={styles.container}>
      <ScopedCommands
        commands={useMemo(
          () => ({
            // avoid hijacking the escape key unless there is some streaming to cancel
            'github:cancel': stoppable ? stopResponse : undefined,
            'copilot-chat:send-message': sendMessage,
            'copilot-chat:send-message-new-conversation': sendMessageToNewThread,
            'copilot-chat:add-attachment': addAttachment,
            'copilot-chat:edit-last-message': textAreaEmpty ? editLastMessage : undefined,
            'copilot-chat:deep-codesearch': setDeepCodeSearch,
          }),
          [
            addAttachment,
            editLastMessage,
            sendMessage,
            sendMessageToNewThread,
            setDeepCodeSearch,
            stopResponse,
            stoppable,
            textAreaEmpty,
          ],
        )}
      >
        <form
          className={clsx(
            styles.chatForm,
            {
              [styles.containerSmall]: props.size === 'small', // Only used by assistive now
              [styles.containerDisabled]: disabled,
            },
            props.className,
          )}
          ref={containerRef}
          {...testIdProps('copilot-chat-input-form')}
        >
          {supportsReferenceAttachments && (
            <ChatInputReferences
              className={styles.attachments}
              returnFocusRef={attachmentButtonRef}
              tokenSize={props.size === 'small' ? 'small' : 'medium'}
              isLoading={mentionReferencesPending}
              onSelectReference={onSelectReference}
              onRemoveReference={props.onRemoveReference}
              getReferenceVersion={props.getReferenceVersion}
              showConvertToFileText={!!pastedFile && !state.isWaitingOnAttachment}
              onConvertToFile={handleConvertToFile}
              onConvertToFileDismiss={handleConvertToFileDismiss}
              hideAttachmentsMenu={!!props.initialReference}
            />
          )}

          <ScopedCommands.LimitKeybindingScope
            // The default bindings for these are 'escape' and 'enter', so we need to strictly limit scope to avoid
            // breaking all the menu and button functionalities that rely on those keys
            commandIds={LIMITED_KEYBINDING_COMMAND_IDS}
            className={clsx(styles.inputContainer, {
              [styles.inputContainerNewConversation]: props.isNewConversation, // Only used by assistive now
            })}
            ref={textAreaPreviewContainerRef}
          >
            <ChatInputAutocomplete
              onSelectReference={addReferenceForMention}
              onShowAgentsDialog={setAttachmentMenuPanel}
              onSelectCommand={onCommandAutoComplete}
              hideAutocomplete={!currentMode.supportsAutocompletion}
              availableCommands={availableCommands}
            >
              <textarea
                autoFocus={!props.disableAutoFocus}
                id={copilotChatTextAreaId}
                className={clsx(styles.input, {[styles.inputDisabled]: disabled})}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                aria-label={currentMode.textAreaAriaLabel || placeholderText}
                aria-multiline="true"
                onPaste={handlePaste}
                ref={textAreaRef}
                onScroll={handleScroll}
                onChange={handleChange}
                onFocus={handleFocus}
                placeholder={placeholderText}
                value={text}
                data-react-autofocus
                disabled={disabled}
                {...testIdProps('copilot-chat-input-textarea')}
              />
            </ChatInputAutocomplete>
            <ChatInputPreview text={text} ref={textareaPreviewRef} availableCommandKeys={availableCommandKeys} />
          </ScopedCommands.LimitKeybindingScope>
          <ChatInputToolbar
            addAttachment={addAttachment}
            currentMode={currentMode}
            attachmentButtonRef={attachmentButtonRef}
            deepCodeSearch={state.skillOptions?.deepCodeSearch}
            modes={chatModes}
            onClickMode={onClickMode}
            stoppable={stoppable}
            submitDisabled={submitDisabled}
            onNewThreadSelected={props.onNewThreadSelected}
            thirdPartyMcpAllowed={state.thirdPartyMcpAllowed}
            supportsAttachments={supportsAttachments}
            disabled={disabled}
            extraToolbar={
              isTask && !props.extraToolbar ? (
                <RepoBranchAgentPicker
                  selectedRepo={selectedRepo}
                  selectedBranch={selectedBranch}
                  selectedAgent={selectedAgent}
                  onRepoSelect={setSelectedRepo}
                  onBranchSelect={setSelectedBranch}
                  onAgentSelect={agent => setSelectedAgent(agent)}
                  agentSelectionEnabled={copilotFeatureFlags.sweAgentUseSubagents}
                  initialRepoNWO={initialRepoNWO}
                />
              ) : (
                props.extraToolbar
              )
            }
            showPlanningToggle={props.showPlanningToggle}
            chatCommand={command}
            isCopilotSpace={isCopilotSpace}
            isAgentTaskSubmitInProgress={isAgentTaskSubmitInProgress}
            isIncompleteCommand={isIncompleteCommand}
            customModelPicker={customModelPicker}
            showStartPullRequestButton={props.showStartPullRequestButton}
          />
        </form>
        {props.showStartPullRequestButton && (
          <SubmitButton
            disabled={submitDisabled}
            label={currentMode.submitLabel}
            isAgentTaskSubmitInProgress={isAgentTaskSubmitInProgress}
            isIncompleteCommand={isIncompleteCommand}
            showStartPullRequestButton={props.showStartPullRequestButton}
            className={clsx(styles.submitButtonOutsideForm)}
          />
        )}
      </ScopedCommands>
      <AttachmentMenu
        inputRef={textAreaRef}
        inputOnChange={handleChange}
        anchorRef={attachmentButtonRef}
        panel={attachmentMenuPanel}
        onPanelChange={setAttachmentMenuPanel}
        initialRepo={lastAttachedRepo}
        onRepoSelect={onRepoAttach}
        side={props.attachmentMenuSide}
      />

      {/*
        Do not add drag-and-drop handlers for ChatInput on a Copilot Space's edit page
        because they would interfere with the page's Spaces Resources attachment handlers.
      */}
      {supportsAttachments && !isCopilotSpace && (
        <FullScreenDragOverlay
          onFilesDrop={handleFileDrop}
          isUploading={state.isWaitingOnAttachment && state.attachmentProcessingType === 'file-upload'}
        />
      )}
    </div>
  )
}

// Toolbar

interface ChatInputToolbarProps {
  addAttachment: () => void
  attachmentButtonRef: RefObject<HTMLButtonElement | null>
  deepCodeSearch: boolean | undefined
  stoppable: boolean
  submitDisabled: boolean
  onNewThreadSelected?: () => Promise<void>
  extraToolbar: React.ReactNode
  thirdPartyMcpAllowed?: boolean
  showPlanningToggle?: boolean
  currentMode: ModeSupport
  modes: ModeSupport[]
  onClickMode?: (modeName: string) => void
  chatCommand: CopilotChatCommandType | null
  supportsAttachments: boolean
  disabled?: boolean
  isCopilotSpace?: boolean | null
  isAgentTaskSubmitInProgress?: boolean
  isIncompleteCommand: boolean
  customModelPicker?: React.ReactNode
  showStartPullRequestButton?: boolean
}

const ChatInputToolbar = memo((props: ChatInputToolbarProps) => {
  const {
    chatCommand,
    extraToolbar,
    currentMode,
    supportsAttachments,
    submitDisabled,
    isCopilotSpace,
    isAgentTaskSubmitInProgress = false,
    isIncompleteCommand,
    showStartPullRequestButton,
  } = props
  const commandSupportsModelPickerAndMcp =
    (chatCommand !== 'task' || copilotFeatureFlags.codingAgentModelSelection) &&
    chatCommand !== 'loop' &&
    chatCommand !== 'spark'

  const showModelPicker = !!(
    props.onNewThreadSelected &&
    currentMode.supportsModelSwitching &&
    commandSupportsModelPickerAndMcp
  )

  const isTask = isTaskCommand(currentMode.name, chatCommand)

  return (
    <div
      className={clsx(styles.toolbar, {
        [styles.toolbarNoModes]: props.modes.length === 1,
      })}
    >
      {/* Modes */}
      {props.modes.length > 1 && (
        <div className={styles.mode}>
          <div className="d-flex">
            <ActionMenu>
              <ActionMenu.Button
                className={styles.modeSelectButton}
                leadingVisual={<currentMode.icon />}
                variant="invisible"
                trailingAction={TriangleDownIcon}
              >
                {currentMode.name}
              </ActionMenu.Button>
              <ActionMenu.Overlay width="auto">
                <ActionList selectionVariant="single" role="menu" aria-label="Item">
                  {props.modes.map(mode => (
                    <ActionList.Item
                      selected={currentMode.name === mode.name}
                      onSelect={() => {
                        props.onClickMode?.(mode.name)
                      }}
                      key={mode.name}
                    >
                      <ActionList.LeadingVisual>{<mode.icon />}</ActionList.LeadingVisual>
                      {mode.name}
                    </ActionList.Item>
                  ))}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
      )}

      <div className={styles.extra}>
        {supportsAttachments &&
          (copilotFeatureFlags.askModeDropdown ? (
            <IconButton
              // eslint-disable-next-line react-hooks/refs
              ref={props.attachmentButtonRef}
              icon={PlusIcon}
              className={styles.attachmentIconButton}
              aria-label={isCopilotSpace ? 'Add files' : 'Add repositories, files, and spaces'}
              // eslint-disable-next-line react-hooks/refs
              onClick={props.addAttachment}
              data-testid="attachment-menu-button"
            />
          ) : (
            <Button
              // eslint-disable-next-line react-hooks/refs
              ref={props.attachmentButtonRef}
              leadingVisual={PlusIcon}
              variant="invisible"
              aria-label={isCopilotSpace ? 'Add files' : 'Add repositories, files, and spaces'}
              className={styles.attachmentButton}
              // eslint-disable-next-line react-hooks/refs
              onClick={props.addAttachment}
              data-testid="attachment-menu-button"
            >
              <span
                className={clsx(styles.attachmentButtonText, {
                  'sr-only': isTask,
                })}
              >
                {isCopilotSpace ? 'Add files' : 'Add repositories, files, and spaces'}
              </span>
            </Button>
          ))}
        {copilotFeatureFlags.showDeepCodeSearchButton && (
          <CommandIconButton
            commandId="copilot-chat:deep-codesearch"
            icon={RocketIcon}
            variant="invisible"
            tooltipDirection="s"
            // eslint-disable-next-line react-hooks/refs
            className={clsx({
              // eslint-disable-next-line react-hooks/refs
              [styles.clicked]: props.deepCodeSearch,
            })}
          />
        )}
        {extraToolbar}
        {!copilotFeatureFlags.spacesAsAttachmentsEnabled && currentMode.supportsSpaces && <SpacesSelect />}
        {
          // eslint-disable-next-line react-hooks/refs
          copilotFeatureFlags.planningAgent && !copilotFeatureFlags.chatInputCommands && props.showPlanningToggle && (
            <PlanningIntentToggle />
          )
        }
      </div>
      <div className={clsx(styles.actions, {[styles.actionsMultiple]: showModelPicker})}>
        {/* eslint-disable-next-line react-hooks/refs */}
        {showModelPicker &&
          // eslint-disable-next-line react-hooks/refs
          (props.customModelPicker || (
            <ModelPicker
              // eslint-disable-next-line react-hooks/refs
              onNewThreadSelected={props.onNewThreadSelected!}
              // eslint-disable-next-line react-hooks/refs
              disabled={props.disabled}
            />
          ))}
        <div className={styles.toolbarButtons}>
          {
            // eslint-disable-next-line react-hooks/refs
            copilotFeatureFlags.copilotThirdPartyMcp &&
              currentMode.supportsMCP &&
              props.thirdPartyMcpAllowed &&
              commandSupportsModelPickerAndMcp && <McpToolMenu />
          }
          {
            // eslint-disable-next-line react-hooks/refs
            props.stoppable ? (
              <StopButton />
            ) : (
              <SubmitButton
                disabled={submitDisabled}
                label={currentMode.submitLabel}
                isAgentTaskSubmitInProgress={isAgentTaskSubmitInProgress}
                isIncompleteCommand={isIncompleteCommand}
                showStartPullRequestButton={showStartPullRequestButton}
                className={showStartPullRequestButton ? styles.submitButtonInForm : undefined}
              />
            )
          }
        </div>
      </div>
    </div>
  )
})
ChatInputToolbar.displayName = 'ChatInputToolbar'

function SubmitButton({
  disabled,
  label,
  isAgentTaskSubmitInProgress = false,
  isIncompleteCommand,
  showStartPullRequestButton,
  className,
}: {
  disabled?: boolean
  label?: string
  isAgentTaskSubmitInProgress?: boolean
  isIncompleteCommand: boolean
  showStartPullRequestButton?: boolean
  className?: string
}) {
  // Determine the appropriate aria-label based on why the button is disabled
  const getAriaLabel = () => {
    if (!disabled) {
      return label || 'Send now'
    }

    // Check if it's disabled because of an incomplete slash command
    if (isIncompleteCommand) {
      return 'Complete your slash command before sending'
    }

    return label || 'Sending a message is unavailable'
  }

  const buttonContent = isAgentTaskSubmitInProgress ? (
    <div className="d-flex flex-items-center height-full mx-2">
      <Spinner size="small" />
    </div>
  ) : showStartPullRequestButton && disabled ? (
    <Button size="medium" leadingVisual={PaperAirplaneIcon} inactive>
      {START_PULL_REQUEST_BUTTON_TEXT}
    </Button>
  ) : disabled ? (
    <IconButton
      variant="invisible"
      size="medium"
      icon={PaperAirplaneIcon}
      aria-label={getAriaLabel()}
      inactive
      aria-disabled
    />
  ) : (
    <CommandIconButton
      commandId="copilot-chat:send-message"
      variant={showStartPullRequestButton ? undefined : 'invisible'}
      size="medium"
      icon={PaperAirplaneIcon}
      aria-label={getAriaLabel()}
      showStartPullRequest={showStartPullRequestButton}
    />
  )

  return className ? <div className={className}>{buttonContent}</div> : buttonContent
}

function StopButton() {
  return (
    <CommandIconButton
      {...testIdProps('copilot-chat-stop-button')}
      commandId="github:cancel"
      variant="invisible"
      size="medium"
      icon={SquareFillIcon}
      tooltipDirection="s"
      className={styles.CommandIconButton}
    />
  )
}

try{ ChatInput.displayName ||= 'ChatInput' } catch {}
try{ SubmitButton.displayName ||= 'SubmitButton' } catch {}
try{ StopButton.displayName ||= 'StopButton' } catch {}